import { createHash } from "node:crypto";
import type { Agent } from "node:http";
import type { LoggerImplement, ResponseData } from "@iflyrpa/share";
import axios, {
    type AxiosError,
    type AxiosInstance,
    type AxiosRequestConfig,
    type AxiosResponse,
    type CreateAxiosDefaults,
} from "axios";
import { USER_MESSAGE } from "./errorMessages";
import { reportFeishuAlarm } from "./feishuAlarm";
import { stringifyError } from "./index";
import { ProxyAgent } from "./proxy";

/** 需要 @所有人 的高危错误：风控挑战、限流、鉴权失效 */
const ALARM_STATUS = new Set([401, 403, 429, 461, 471]);

/**
 * 判断是否为本机地址（localhost / 回环 IP / .local 等）。
 *
 * 本机地址上的失败来自本地服务或单元测试，不是线上问题，不该进告警群。
 * 用 URL 解析而非字符串包含匹配：`https://api.example.com/?cb=localhost` 这类
 * 参数里带 localhost 的线上地址不能被误判成本机。
 */
export function isLocalAddress(url: string): boolean {
    let hostname: string;
    try {
        hostname = new URL(url).hostname.toLowerCase();
    } catch {
        // 解析不了（相对路径等）就不当作本机，宁可多报也不要漏掉线上问题
        return false;
    }

    // IPv6 回环 ::1 经 URL 解析后带方括号
    if (hostname === "localhost" || hostname === "::1" || hostname === "[::1]") return true;
    if (hostname.endsWith(".localhost") || hostname.endsWith(".local")) return true;
    // 整个 127.0.0.0/8 都是回环
    if (/^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
    if (hostname === "0.0.0.0") return true;

    return false;
}

/**
 * 常见 HTTP 状态码 → 用户可读提示。
 * axios 对 4xx 抛 ERR_BAD_REQUEST、5xx 抛 ERR_BAD_RESPONSE，两者共用这份映射。
 */
const HTTP_STATUS_MESSAGE: Record<number, string> = {
    400: "请求参数错误，请检查参数格式是否正确！",
    401: "登录状态已失效，请重新登录后重试！",
    403: "没有访问权限，账号可能受限或签名已失效！",
    404: "请求的资源不存在，请检查接口地址！",
    405: "请求方法不被允许，请检查接口调用方式！",
    406: "服务端无法返回可接受的内容格式！",
    407: "代理需要身份验证，请检查代理配置！",
    408: "请求超时，请检查网络连接！",
    409: "请求冲突，资源状态已变更，请刷新后重试！",
    410: "请求的资源已被移除！",
    412: "请求前置条件不满足，请刷新后重试！",
    413: "提交内容过大，请压缩或分批后重试！",
    414: "请求地址过长，请检查参数！",
    415: "不支持的内容类型，请检查请求格式！",
    422: "请求内容校验失败，请检查参数内容！",
    423: "资源已被锁定，请稍后重试！",
    429: "请求过于频繁，请稍后重试！",
    431: "请求头过大，请清理 Cookie 后重试！",
    451: "内容不合规或因法律原因被拒绝！",
    // 小红书风控验证码挑战：461/471 并非参数错误，响应体通常是空的
    // {"code":0,"success":true,"data":{}}，验证信息在 Verifytype/Verifyuuid 响应头里。
    461: "账号可能受限，请在网页上完成验证后重试！",
    471: "账号可能受限，请在网页上完成验证后重试！",
    500: "服务器内部错误，请稍后重试！",
    501: "服务端不支持该请求，请稍后重试！",
    502: "网关错误，请稍后重试！",
    503: "服务暂时不可用，请稍后重试！",
    504: "网关超时，请稍后重试！",
    507: "服务端存储空间不足，请稍后重试！",
    509: "服务带宽超限，请稍后重试！",
    520: "服务端返回未知错误，请稍后重试！",
    521: "源站拒绝连接，请稍后重试！",
    522: "源站连接超时，请稍后重试！",
    524: "源站响应超时，请稍后重试！",
};

export class Http {
    private readonly apiClient: AxiosInstance;
    public logger?: LoggerImplement;
    public proxyInfo?: string;
    private readonly platform?: string; // 业务标识（xiaohongshu、douyin、weixin等）
    private readonly agentPromise?: Promise<{
        ip: string;
        port: string;
        agent: Agent | null;
    } | null> = undefined;

    static handleApiError(error: unknown): ResponseData {
        // 如果错误已经是 ResponseData 格式，直接抛出。
        // 注意只判断键存在是不够的：服务端返回 {code, message: undefined} 时会原样透传，
        // 下游 `${error.message}` 就会打出字面量 "undefined"，因此这里补一个文案兜底。
        if (error && typeof error === "object" && "code" in error && "message" in error) {
            const resp = error as ResponseData;
            if (typeof resp.message !== "string" || !resp.message.trim()) {
                return { ...resp, message: USER_MESSAGE.SYSTEM_ERROR };
            }
            return resp;
        }

        // 处理其他类型的错误：用户侧给统一兜底文案，原始 error 仍保留在 data 里便于排查
        return {
            code: 500,
            message: USER_MESSAGE.SYSTEM_ERROR,
            data: error,
        };
    }

    constructor(
        config?: CreateAxiosDefaults,
        logger?: LoggerImplement,
        addr?: string | null,
        accountId?: number,
        platform?: string, // 业务标识，如 "xiaohongshu"、"douyin"、"weixin" 等
    ) {
        this.apiClient = axios.create({
            ...config,
            // proxy: {
            //     host: "127.0.0.1", // 根据你的代理服务设置
            //     port: 8888,
            //     protocol: "http", // 可选
            // },
        });
        this.logger = logger;
        this.platform = platform;

        if (addr) this.agentPromise = ProxyAgent({ logger }, addr, accountId);

        this.addResponseInterceptor(() => undefined);
    }

    public clearResponseInterceptors(): void {
        const interceptors = axios.interceptors.response as unknown as {
            handlers: unknown[];
            clear: () => void;
        };

        // 清除之前的拦截器
        if (interceptors.handlers.length > 0) {
            axios.interceptors.response.clear();
        }
    }

    // 添加响应拦截器
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    public addResponseInterceptor<T = any>(findError: (response: AxiosResponse<T>) => ResponseData | undefined): void {
        const interceptors = this.apiClient.interceptors.response as unknown as {
            handlers: unknown[];
            clear: () => void;
        };

        // 清除之前的拦截器，避免重复添加
        if (interceptors.handlers.length > 0) {
            this.apiClient.interceptors.response.clear();
        }

        this.apiClient.interceptors.response.use(
            (response) => {
                // 检查响应中的 errno 字段
                const error = findError(response);

                if (error) {
                    return Promise.reject(
                        Object.assign(error, {
                            extra: {
                                method: response.config.method,
                                url: response.config.url,
                            },
                        }),
                    );
                }

                return response;
            },
            (error: AxiosError) => {
                const foundError = findError(error.response as AxiosResponse<T>);

                // 创建标准错误响应格式
                const errorResponse: ResponseData = {
                    // 单独拦截401的状态码，映射为400，避免触发绘文平台的鉴权错误处理逻辑
                    code: error.response?.status || 500,
                    message: error.message || "",
                    data: error.response?.data,
                    extra: {
                        method: error.config?.method,
                        url: error.config?.url,
                        isProxyRequest: !!(error.config && (error.config.httpAgent || error.config.httpsAgent)),
                        params: error.config?.params,
                        data: error.config?.data,
                        serverDate: error.response?.headers?.date, // 服务端时间（用于时钟漂移检测）
                    },
                };

                if (foundError) Object.assign(errorResponse, foundError);

                if (error.response?.status === 403) {
                    this.logger?.warn(`[403 详细信息] URL: ${error.config?.url}`);
                    this.logger?.warn(`[403 响应头] ${JSON.stringify(error.response?.headers || {})}`);
                    this.logger?.warn(`[403 响应体] ${JSON.stringify(error.response?.data || null)}`);
                    const verifyDecision = error.response?.headers?.["x-tt-verify-passport-decision"];
                    if (verifyDecision) {
                        this.logger?.warn(`[403 验证决策] x-tt-verify-passport-decision: ${verifyDecision}`);
                    }
                }

                // 小红书风控验证码挑战：461/471 的响应体为空对象，唯一有效信息在响应头
                // Verifytype / Verifyuuid（另有 Verifybiz / Verifyredirecturl），必须打出来才能定位。
                if (error.response?.status === 461 || error.response?.status === 471) {
                    const h = (error.response?.headers ?? {}) as Record<string, unknown>;
                    const pick = (name: string) => h[name] ?? h[name.toLowerCase()];
                    this.logger?.warn(
                        `[${error.response.status} 风控验证] URL: ${error.config?.url} ` +
                            `Verifytype: ${pick("Verifytype") ?? "-"} ` +
                            `Verifyuuid: ${pick("Verifyuuid") ?? "-"} ` +
                            `Verifybiz: ${pick("Verifybiz") ?? "-"}`,
                    );
                    this.logger?.warn(`[${error.response.status} 响应头] ${JSON.stringify(h)}`);
                    errorResponse.extra = {
                        ...errorResponse.extra,
                        verifyType: pick("Verifytype"),
                        verifyUuid: pick("Verifyuuid"),
                        verifyBiz: pick("Verifybiz"),
                    };
                }

                // 如果服务端返回了详细错误信息，可以使用服务端的错误信息
                if (error.response?.data) {
                    if (typeof error.response.data === "object") {
                        // 如果服务端返回的是对象，可以尝试提取错误信息
                        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                        const serverError = error.response.data as any;

                        errorResponse.message = serverError.msg || serverError.message; // 匹配小红书与普通错误message

                        // 可以保留原始错误数据
                        errorResponse.data = serverError;
                    }
                }
                let _message = error.message || "";
                // ✅ 情况二：网络错误（没有 response）
                if (error.code) {
                    errorResponse.code = errorResponse.code === 401 ? 414 : errorResponse.code; // 屏蔽401错误，避免触发绘文平台鉴权逻辑

                    switch (error.code) {
                        case "ECONNABORTED":
                            _message = "请求超时，请检查网络连接！";
                            break;
                        case "ECONNRESET":
                            _message = "连接被重置，请检查网络或稍后重试！";
                            break;
                        case "ENOTFOUND":
                            _message = "域名解析失败，请检查 URL 是否正确！";
                            break;
                        case "ETIMEDOUT":
                            _message = "网络请求超时，请检查网络连接！";
                            break;
                        case "ECONNREFUSED":
                            _message = "服务器拒绝连接，请稍后重试！";
                            break;
                        case "EAI_AGAIN":
                            _message = "DNS 查询超时，请稍后重试！";
                            break;
                        case "ERR_BAD_REQUEST": {
                            // 根据 HTTP 状态码细化错误提示
                            const status = error.response?.status;
                            _message =
                                (status && HTTP_STATUS_MESSAGE[status]) || `请求失败，状态码${status ?? "unknown"}！`;
                            break;
                        }
                        case "ERR_BAD_RESPONSE": {
                            const status = error.response?.status;
                            _message =
                                (status && HTTP_STATUS_MESSAGE[status]) ||
                                `服务器响应异常 (${status ?? "unknown"})，请稍后重试！`;
                            break;
                        }
                        case "ERR_CANCELED":
                            errorResponse.code = 414; // 阻止前端报错
                            _message = "请求连接超时，请稍候重试！";
                            if (errorResponse.extra?.isProxyRequest) {
                                _message = "代理连接建立超时，请更换区域或稍后重试！";
                                errorResponse.code = 599;
                                errorResponse.data = this.proxyInfo
                                    ? createHash("md5").update(this.proxyInfo.split(":")[0]).digest("hex") +
                                      this.proxyInfo.split(":")[1]
                                    : {};
                            }
                            break;
                        default: {
                            this.logger?.debug(`未处理的网络错误代码: ${error.code} ${error.message}`, {
                                errorString: stringifyError(error),
                            });
                            // 未知 axios error code 但带状态码时，仍优先给出状态码对应的可读提示；
                            // 兜底文案不拼 error.message —— 原始信息已由上面的 logger.debug 记录
                            const status = error.response?.status;
                            _message = (status && HTTP_STATUS_MESSAGE[status]) || USER_MESSAGE.NETWORK_ERROR;
                            break;
                        }
                    }
                }
                // 网络层错误（无服务端响应体）：errorResponse.message 此时仍是 axios 原始文案
                // （如 "timeout of 3000ms exceeded"），应优先使用上面归一化后的友好文案 _message。
                // 有服务端响应体时才保留服务端返回的 message。
                if (error.code && !error.response?.data) {
                    errorResponse.message = _message || errorResponse.message;
                } else {
                    errorResponse.message = errorResponse.message ? errorResponse.message : _message;
                }

                if (errorResponse.message === "canceled") {
                    const message = errorResponse.extra?.isProxyRequest
                        ? "代理连接建立超时，请更换区域或稍后重试！"
                        : "网络请求超时，请稍后重试！";
                    errorResponse.message = message;
                }

                if (error.message.includes("Proxy connection ended")) {
                    // 代理Connect阶段错误重写！
                    errorResponse.message = "所在区域代理连接超时，请更换区域或稍后重试！";
                }

                // 告警所需的原始信息挂到 extra 上，由 api() 在重试彻底耗尽后再上报。
                // 不在拦截器里直接上报：拦截器对每一次失败尝试都会触发，重试成功的请求
                // 用户无感知，却已经把告警群刷了一轮。
                errorResponse.extra = {
                    ...errorResponse.extra,
                    alarmStatus: error.response?.status,
                    alarmErrorCode: error.code,
                };

                // 返回标准化的错误响应
                throw errorResponse;
            },
        );
    }

    /**
     * 请求最终失败（重试耗尽或错误不可重试）后上报飞书告警。
     * 告警所需的 status / axios error code 由响应拦截器写入 error.extra。
     *
     * 两类请求不上报：本机地址（localhost / 127.0.0.1 等）与拿不到 URL 的错误。
     * 前者是本地服务或测试用例，后者没有目标地址无法定位，都属于告警噪音。
     */
    private reportRequestFailure(config: AxiosRequestConfig, error: ResponseData, attempts: number): void {
        const status = error.extra?.alarmStatus as number | undefined;
        const axiosCode = error.extra?.alarmErrorCode as string | undefined;
        const method = (config.method || "get").toUpperCase();

        // 拼上 baseURL：不少调用点用 baseURL + 相对 url（如 sessionCheck），
        // 只取 config.url 会让 stage 退化成看不出目标服务的相对路径甚至 "-"。
        const baseURL = config.baseURL || this.apiClient.defaults.baseURL || "";
        const fullUrl = config.url
            ? /^https?:\/\//.test(config.url)
                ? config.url
                : `${baseURL.replace(/\/$/, "")}${config.url}`
            : baseURL;

        // 无 URL：定位不到目标接口，上报了也只是一条 "GET -" 的噪音
        if (!fullUrl) {
            this.logger?.debug(`[告警跳过] 请求失败但无 URL，不上报: ${error.message}`);
            return;
        }

        // 本机地址：本地服务 / 单元测试，不是线上问题
        if (isLocalAddress(fullUrl)) {
            this.logger?.debug(`[告警跳过] 本机地址不上报: ${fullUrl}`);
            return;
        }

        // error.message 可能是 undefined（服务端返回 {code} 但无 msg/message 字段），
        // 直接模板拼接会在群里打出字面量 "undefined"，对排查毫无价值。
        const rawMsg = typeof error.message === "string" && error.message.trim() ? error.message : "";
        const baseMsg = rawMsg || `请求失败（无错误文案，code=${error.code ?? "unknown"}）`;
        const retriedSuffix = attempts > 0 ? `（已重试${attempts}次仍失败）` : "";

        reportFeishuAlarm({
            level: status && ALARM_STATUS.has(status) ? "alarm" : "warning",
            platform: "@iflyrpa/playwright",
            source: "http",
            stage: `${method} ${fullUrl}`,
            errorType: status ? `HTTP_${status}` : axiosCode || "NETWORK_ERROR",
            code: error.code,
            msg: `${baseMsg}${retriedSuffix}`,
            url: fullUrl,
            title: "RPA接口异常",
        });
    }

    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    public async api<T = any>(
        config: AxiosRequestConfig,
        options?: {
            retries?: number;
            retryDelay?: number;
            timeout?: number;
            onUploadProgress?: (progressEvent: any) => void;
            /**
             * 外部取消信号（用于用户主动取消上传等场景）。
             * 注意：不能直接用 config.signal —— 下面会用内部 AbortController 覆盖它做超时兜底，
             * 所以外部信号必须在这里传入，由内部 controller 代理转发。
             */
            signal?: AbortSignal;
        },
    ): Promise<T> {
        const retries = options?.retries ?? 0; // 默认不重试
        const retryDelay = options?.retryDelay ?? 500;
        const reqTimeout = options?.timeout ?? 60 * 1000; // 60s 默认超时
        const externalSignal = options?.signal;
        let agent: { ip: string; port: string; agent: Agent | null } | null | undefined;
        // let isForceRefreshProxy = false;
        const sessionRt = async (Rtimes: number): Promise<T> => {
            try {
                // if (this.ip) {
                // 	const cacheKey = `${this.ip}|${this.adr ?? ""}|${this.accountId ?? ""}`;
                //
                //     if( this.adr && Rtimes === 1 ) {
                //         this.adr = "";
                //         Http.agentCache.delete(cacheKey);
                //     }
                //     if( Rtimes%2 === 0 && Rtimes > 0 ) {
                //         // 重试时清除缓存，并强制刷新代理 refresh = true
                //         Http.agentCache.delete(cacheKey);
                //         isForceRefreshProxy = true;
                //         this.task.debug && this.task.logger.info(`进入第${Rtimes+1}次重试，强制刷新代理！`);
                //     }
                //
                //     if (Http.agentCache.has(cacheKey)) {
                //         this.agent = Http.agentCache.get(cacheKey) ?? null;
                //
                //     } else if (Http.agentPromiseCache.has(cacheKey)) {
                //         this.task.debug && this.task.logger.info("代理正在创建，等待中...");
                //         const agentResult = await Http.agentPromiseCache.get(cacheKey);
                //         this.agent = agentResult?.agent ?? null;
                //     } else {
                //         const promise = ProxyAgent(this.ip, this.adr, this.accountId, isForceRefreshProxy);
                //
                //         Http.agentPromiseCache.set(cacheKey, promise);
                //
                //         this.agent = (await promise)?.agent;
                //
                //         if (this.agent) {
                //             this.agent.options.keepAlive = true;
                //             Http.agentCache.set(cacheKey, this.agent);
                //         } else {
                //             Http.agentCache.set(cacheKey, null);
                //         }
                //         Http.agentPromiseCache.delete(cacheKey);
                //     }
                // }
                //
                // if (this.agent === null) {
                //     if (Rtimes === retries && this.ip) {
                //         return Promise.reject({
                //             code: 400,
                //             message: "",
                //             data: {
                //                 code: 400, // 仅配合sessioncheck使用
                //                 msg: "当前所选区域暂不支持!"
                //             }
                //         });
                //     }
                //     if (this.adr) {
                //        throw {code: 500, message: "", data: {}}; // 触发重试
                //     }
                // }

                this.proxyInfo = agent ? `${agent.ip}:${agent.port}` : undefined;

                // 请求级别超时控制（即使代理层没超时，也能强制中断）
                // 注意：AbortController 的兜底超时必须比 axios 自身 timeout 略长，
                // 否则二者同时到点时 AbortController 会先触发 ERR_CANCELED(code 414)，
                // 导致超时错误落不进重试白名单 [0,500,502,503,504,599]，使 retries 形同虚设。
                // 让 axios 先以 ECONNABORTED(code 500) 触发常规 socket 超时即可正常重试，
                // AbortController 仅作为 DNS/连接阶段卡死的最终兜底。
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), reqTimeout + 500); // 全阶段超时兜底

                // 外部取消信号代理到内部 controller：外部一 abort，本次请求立即中断
                const forwardAbort = () => controller.abort();
                if (externalSignal) {
                    if (externalSignal.aborted) controller.abort();
                    else externalSignal.addEventListener("abort", forwardAbort, { once: true });
                }

                const response = await this.apiClient<T>({
                    ...config,
                    timeout: reqTimeout, // Axios 自身的超时 http阶段，tcp socket已建立情况下的超时
                    signal: controller.signal, // 自定义超时保护信号，用户axios监听
                    onUploadProgress: options?.onUploadProgress, // 透传上传进度回调
                    ...(agent ? { httpAgent: agent.agent, httpsAgent: agent.agent } : {}),
                }).finally(() => {
                    clearTimeout(timeoutId);
                    externalSignal?.removeEventListener("abort", forwardAbort);
                });

                return response.data;
            } catch (error) {
                const handledError: ResponseData = Http.handleApiError(error);
                const isRetry = [0, 500, 502, 503, 504, 599].includes(handledError.code);

                if (Rtimes < retries && isRetry) {
                    const url = config.url || "";
                    // 指数退避：对端超时/5xx 时固定小间隔连打只会加重拥塞，
                    // 上限 5s 避免把单次调用的总耗时拖得过长。
                    const backoff = Math.min(retryDelay * 2 ** Rtimes, 5000);
                    this.logger?.warn(
                        `进入第${Rtimes + 1}次重试！错误码: ${handledError.code}, 等待: ${backoff}ms, 请求地址: ${url}`,
                    );
                    await new Promise((resolve) => setTimeout(resolve, backoff));
                    return sessionRt(Rtimes + 1);
                }
                // 重试全部耗尽（或该错误不可重试）才上报：此时用户确实感知到了失败
                this.reportRequestFailure(config, handledError, Rtimes);
                return Promise.reject(handledError);
            }
        };
        if (this.agentPromise) {
            const requestUrl = config.url || "";
            const platformTag = this.platform ? `[${this.platform}]` : "";
            this.logger?.info(`${platformTag}[代理请求] 接口: ${requestUrl}`);
            agent = await this.agentPromise;
            if (agent) {
                this.logger?.info(`${platformTag}[代理请求] 代理地址: ${agent.ip}:${agent.port}`);
            }
        }

        return sessionRt(0);
    }
}
